import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, Trash2, UserPlus, Crown, History } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admins")({ component: Page });

type Role = "root_admin" | "admin" | "user";

function Page() {
  const qc = useQueryClient();
  const [meRoot, setMeRoot] = useState<boolean | null>(null);
  const [me, setMe] = useState<{ id: string; email: string } | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("admin");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) { setMeRoot(false); return; }
      setMe({ id: u.user.id, email: u.user.email ?? "" });
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
      setMeRoot(!!data?.some((r: any) => r.role === "root_admin"));
    })();
  }, []);

  const { data } = useQuery({
    queryKey: ["admins-list"],
    queryFn: async () => {
      const { data: roles } = await supabase.from("user_roles").select("id, user_id, role, created_at").order("created_at", { ascending: false });
      const ids = Array.from(new Set((roles ?? []).map((r: any) => r.user_id)));
      const { data: profs } = ids.length
        ? await supabase.from("profiles").select("id, email, display_name").in("id", ids)
        : { data: [] as any[] };
      const map = new Map((profs ?? []).map((p: any) => [p.id, p]));
      return (roles ?? []).map((r: any) => ({ ...r, profile: map.get(r.user_id) }));
    },
    enabled: meRoot === true,
  });

  const { data: auditLogs } = useQuery({
    queryKey: ["admin-audit-logs"],
    queryFn: async () => (await supabase.from("admin_audit_logs").select("*").order("created_at", { ascending: false }).limit(100)).data ?? [],
    enabled: meRoot === true,
  });

  const writeAudit = async (entry: { target_user_id?: string | null; target_email?: string | null; action: string; old_role?: string | null; new_role?: string | null; detail?: any }) => {
    if (!me) return;
    await supabase.from("admin_audit_logs").insert({
      actor_user_id: me.id,
      actor_email: me.email,
      target_user_id: entry.target_user_id ?? null,
      target_email: entry.target_email ?? null,
      action: entry.action,
      old_role: entry.old_role ?? null,
      new_role: entry.new_role ?? null,
      detail: entry.detail ?? null,
    });
    qc.invalidateQueries({ queryKey: ["admin-audit-logs"] });
  };

  const addRole = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data: prof } = await supabase.from("profiles").select("id").eq("email", email.trim()).maybeSingle();
      if (!prof) { toast.error("ไม่พบผู้ใช้นี้ (ต้องสมัครเข้าระบบก่อน)"); return; }
      const { error } = await supabase.from("user_roles").insert({ user_id: prof.id, role });
      if (error) throw error;
      await writeAudit({ target_user_id: prof.id, target_email: email.trim(), action: "add_role", new_role: role });
      toast.success("เพิ่มสิทธิ์เรียบร้อย");
      setEmail("");
      qc.invalidateQueries({ queryKey: ["admins-list"] });
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const removeRole = async (id: string, row: any) => {
    if (!confirm("ลบสิทธิ์นี้?")) return;
    const { error } = await supabase.from("user_roles").delete().eq("id", id);
    if (error) return toast.error(error.message);
    await writeAudit({ target_user_id: row.user_id, target_email: row.profile?.email, action: "remove_role", old_role: row.role });
    toast.success("ลบสิทธิ์แล้ว");
    qc.invalidateQueries({ queryKey: ["admins-list"] });
  };

  const changeRole = async (id: string, newRole: Role, row: any) => {
    if (row.role === newRole) return;
    const { error } = await supabase.from("user_roles").update({ role: newRole }).eq("id", id);
    if (error) return toast.error(error.message);
    await writeAudit({ target_user_id: row.user_id, target_email: row.profile?.email, action: "change_role", old_role: row.role, new_role: newRole });
    toast.success("เปลี่ยน role แล้ว");
    qc.invalidateQueries({ queryKey: ["admins-list"] });
  };

  if (meRoot === null) return <div className="text-muted-foreground">กำลังตรวจสอบสิทธิ์…</div>;
  if (!meRoot) return (
    <Card><CardHeader><CardTitle>ไม่มีสิทธิ์เข้าถึง</CardTitle><CardDescription>หน้านี้สำหรับ root_admin เท่านั้น</CardDescription></CardHeader></Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Shield className="h-6 w-6 text-primary"/>จัดการผู้ดูแล</h1>
        <p className="text-sm text-muted-foreground">เพิ่ม / ลบสิทธิ์ root_admin, admin, user (เฉพาะ root_admin เท่านั้น)</p>
      </div>

      <Card>
        <CardHeader><CardTitle>เพิ่มสิทธิ์ผู้ใช้</CardTitle><CardDescription>ผู้ใช้ต้องสมัครและล็อกอินอย่างน้อยหนึ่งครั้งเพื่อให้ระบบรู้จัก</CardDescription></CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1"/>
          <Select value={role} onValueChange={(v) => setRole(v as Role)}>
            <SelectTrigger className="w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="root_admin">root_admin</SelectItem>
              <SelectItem value="admin">admin</SelectItem>
              <SelectItem value="user">user</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={addRole} disabled={busy}><UserPlus className="h-4 w-4 mr-1"/>เพิ่ม</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>รายชื่อสิทธิ์ทั้งหมด</CardTitle><CardDescription>{data?.length ?? 0} รายการ</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>อีเมล</TableHead><TableHead>ชื่อ</TableHead><TableHead>Role</TableHead><TableHead>เพิ่มเมื่อ</TableHead><TableHead className="text-right">การกระทำ</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data ?? []).map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.profile?.email ?? r.user_id}</TableCell>
                  <TableCell className="text-sm">{r.profile?.display_name ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {r.role === "root_admin" && <Crown className="h-3 w-3 text-primary"/>}
                      <Select value={r.role} onValueChange={(v) => changeRole(r.id, v as Role, r)}>
                        <SelectTrigger className="h-8 w-36"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="root_admin">root_admin</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                          <SelectItem value="user">user</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("th-TH")}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => removeRole(r.id, r)}><Trash2 className="h-3 w-3"/></Button>
                  </TableCell>
                </TableRow>
              ))}
              {(data ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">ยังไม่มีข้อมูล</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><History className="h-4 w-4"/>Audit Log</CardTitle>
          <CardDescription>บันทึกการเปลี่ยนสิทธิ์ผู้ดูแล (ล่าสุด 100 รายการ)</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>เวลา</TableHead><TableHead>ผู้ทำรายการ</TableHead><TableHead>การกระทำ</TableHead><TableHead>เป้าหมาย</TableHead><TableHead>เปลี่ยน</TableHead></TableRow></TableHeader>
            <TableBody>
              {(auditLogs ?? []).map((l: any) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString("th-TH")}</TableCell>
                  <TableCell className="text-xs font-mono">{l.actor_email ?? l.actor_user_id}</TableCell>
                  <TableCell><Badge variant="outline">{l.action}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">{l.target_email ?? l.target_user_id ?? "—"}</TableCell>
                  <TableCell className="text-xs">
                    {l.old_role && <Badge variant="outline" className="mr-1">{l.old_role}</Badge>}
                    {l.old_role && l.new_role && <span className="text-muted-foreground">→</span>}
                    {l.new_role && <Badge className="ml-1">{l.new_role}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
              {(auditLogs ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">ยังไม่มีการเปลี่ยนแปลง</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}